import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      <div className="max-w-3xl mx-auto px-4 py-8 sm:py-12">
        <div className="mb-8">
          <Link to="/" className="text-white/70 hover:text-white text-sm inline-flex items-center gap-1">
            ← Back to Home
          </Link>
        </div>

        <header className="mb-10">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Privacy Policy</h1>
          <p className="text-white/70">RestroAI – AI-Powered Restaurant Management Platform (com.restroai.app)</p>
          <p className="text-white/50 text-sm mt-2">Last updated: February 2026</p>
          <p className="text-white/60 text-sm mt-4 rounded-lg border border-white/20 bg-white/5 px-3 py-2">
            Looking for <strong>PocketBillingAI</strong> (Google Play app <code className="text-xs">in.restroai.lite</code>)?{' '}
            <Link to="/privacy-policy" className="text-blue-300 hover:text-blue-200 underline">
              PocketBillingAI Privacy Policy
            </Link>
          </p>
        </header>

        <div className="space-y-8 text-white/90 leading-relaxed">
          <section>
            <h2 className="text-xl font-semibold text-white mb-3">1. Introduction</h2>
            <p>
              RestroAI (&quot;we&quot;, &quot;our&quot;, or &quot;us&quot;) operates the RestroAI mobile application and related services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our app and services. By using RestroAI, you agree to the practices described in this policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">2. Information We Collect</h2>
            <p className="mb-3">We may collect the following types of information:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong className="text-white">Account information:</strong> Name, email address, phone number, and password when you register or sign in.</li>
              <li><strong className="text-white">Restaurant and business data:</strong> Restaurant name, address, menu items, tables, staff details, and operational data you provide.</li>
              <li><strong className="text-white">Order and transaction data:</strong> Orders placed, order history, and related billing or payment information where applicable.</li>
              <li><strong className="text-white">Device information:</strong> Device type, operating system, and unique identifiers (e.g. for push notifications) to deliver and improve the app.</li>
              <li><strong className="text-white">Usage data:</strong> How you use the app (e.g. features used, analytics) to improve our services.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">3. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Provide, maintain, and improve the RestroAI app and services.</li>
              <li>Process orders, manage your restaurant operations, and support staff and customer flows.</li>
              <li>Send you push notifications (e.g. new orders, waiter requests) if you have enabled them.</li>
              <li>Respond to your requests, support, and feedback.</li>
              <li>Send important service updates and, with your consent, marketing communications.</li>
              <li>Analyse usage to improve performance, security, and user experience.</li>
              <li>Comply with applicable laws and protect our rights and the rights of our users.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">4. Data Sharing and Third Parties</h2>
            <p>
              We do not sell your personal information. We may share your data only in these circumstances: (1) with service providers who help us operate the app (e.g. hosting, push notifications, analytics), under strict confidentiality; (2) when required by law or to protect rights and safety; (3) in connection with a merger, sale, or transfer of assets, with notice where required. We use industry-standard practices to ensure third parties handle your data appropriately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">5. Data Security</h2>
            <p>
              We use appropriate technical and organisational measures to protect your data. Data in transit is encrypted (e.g. HTTPS). We restrict access to personal data to those who need it to provide and support our services. While we strive to protect your information, no method of transmission or storage is 100% secure; we encourage you to use strong passwords and keep your account details secure.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">6. Data Retention</h2>
            <p>
              We retain your information for as long as your account is active or as needed to provide you services, comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your account and associated data subject to applicable law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">7. Your Rights and Choices</h2>
            <p className="mb-3">Depending on your location, you may have the right to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Access, correct, or update your personal information through the app or by contacting us.</li>
              <li>Request deletion of your data, subject to legal and operational requirements.</li>
              <li>Opt out of marketing communications or certain data uses where applicable.</li>
              <li>Disable push notifications in your device settings.</li>
              <li>Lodge a complaint with a supervisory authority if you believe we have not handled your data properly.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">8. Children&apos;s Privacy</h2>
            <p>
              RestroAI is not directed at children under 13. We do not knowingly collect personal information from children under 13. If you believe we have collected such information, please contact us and we will take steps to delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the updated policy on this page and update the &quot;Last updated&quot; date. For material changes, we may notify you via the app or email where appropriate. Continued use of RestroAI after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-white mb-3">10. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <ul className="list-none mt-2 space-y-1">
              <li><strong className="text-white">Website:</strong> <a href="https://www.restroai.in" className="text-blue-300 hover:text-blue-200 underline" target="_blank" rel="noopener noreferrer">https://restroai.in</a></li>
              <li><strong className="text-white">Contact page:</strong> <Link to="/contact-us" className="text-blue-300 hover:text-blue-200 underline">RestroAI Contact Us</Link></li>
            </ul>
          </section>
        </div>

        <footer className="mt-12 pt-8 border-t border-white/20 text-white/60 text-sm text-center">
          <p>© {new Date().getFullYear()} RestroAI. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/" className="text-blue-300 hover:text-blue-200">Home</Link>
            {' · '}
            <Link to="/terms-of-service" className="text-blue-300 hover:text-blue-200">Terms of Service</Link>
            {' · '}
            <Link to="/contact-us" className="text-blue-300 hover:text-blue-200">Contact</Link>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
